import React from "react";
import { Box, IconButton, Text } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { FiX, FiMinimize2, FiMaximize2 } from "react-icons/fi";

const PersistentComponent = ({
  children,
  showPersistentComponent,
  setShowPersistentComponent,
  link,
  condFunc,
}) => {
  const navigate = useNavigate();

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [size, setSize] = useState({ width: 400, height: 600 });
  const [isMinimized, setIsMinimized] = useState(true);

  useEffect(() => {
    // Set default condition to false in localStorage on mount
    localStorage.setItem("condition", JSON.stringify(false));

    // const savedPosition = JSON.parse(localStorage.getItem("position"));
    const savedShow = JSON.parse(
      localStorage.getItem("showPersistentComponent")
    );
    
    // Load saved size
    const savedSize = JSON.parse(localStorage.getItem("size"));
    if (savedSize) {
      setSize(savedSize);
    }

    // if (savedPosition) {
    //   setPosition(savedPosition);
    // } else {
    // Calculate a default top-right position
    // const assumedWidth = 150;
    // const initialX = window.innerWidth - assumedWidth - 20;
    // setPosition({ x: initialX, y: 0 });
    // }

    if (typeof savedShow === "boolean") setShowPersistentComponent(savedShow);
  }, [setShowPersistentComponent]);

  // Listen for auto-minimize audio recorder event
  useEffect(() => {
    const handleMinimizeAudioRecorder = () => {
      console.log("🎤 Auto-minimizing audio recorder from PersistentComponent");
      
      // Position the modal to the top right, same as manual minimize
      const newX = window.innerWidth - 320; // 320px from right edge (300px width + 20px margin)
      const newY = 20; // 20px from top edge
      setPosition({ x: newX, y: newY });
      
      setIsMinimized(true);
      console.log("🎤 Auto-minimize: Positioned to top right", { x: newX, y: newY });
    };

    window.addEventListener('minimizeAudioRecorder', handleMinimizeAudioRecorder);
    
    return () => {
      window.removeEventListener('minimizeAudioRecorder', handleMinimizeAudioRecorder);
    };
  }, []);

  const handleStart = () => {
    setDragging(false);
  };

  const handleDrag = () => {
    setDragging(true);
  };

  const handleStop = (e, data) => {
    setPosition({ x: data.x, y: data.y });
    // localStorage.setItem("position", JSON.stringify({ x: data.x, y: data.y }));

    // Only navigate if we're not on a document page and not dragging
    if (!dragging && link) {
      const currentPath = window.location.pathname;
      const isOnDocumentPage = currentPath.includes('/edit-document') || 
                              currentPath.includes('/view-document');
      
      if (!isOnDocumentPage) {
        navigate(`/patient/${link}`);
      } else {
        console.log("PersistentComponent: On document page, preventing navigation to patient page");
      }
    }
  };

  const handleResize = (e, data) => {
    const { size: newSize } = data;
    setSize(newSize);
    localStorage.setItem("size", JSON.stringify(newSize));
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isMinimized) {
      // When minimizing, move to the right side of the screen
      const newX = window.innerWidth - 320; // 320px from right edge (300px width + 20px margin)
      const newY = 20; // 20px from top edge
      setPosition({ x: newX, y: newY });
    }
    
    setIsMinimized(!isMinimized);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    setShowPersistentComponent(false);
    localStorage.setItem("condition", JSON.stringify(false));
    // Disable live services when popup is closed to prevent credit waste
    localStorage.setItem("liveTranscriptionEnabled", "false");
    localStorage.setItem("liveFactExtractionEnabled", "false");
    console.log(`closed popup`);
    
    // Dispatch a custom event to notify AudioRecorder to stop recording
    window.dispatchEvent(new CustomEvent('popupClosed'));
    
    if (condFunc) {
      condFunc(false);
    }
  };

  if (!showPersistentComponent) return null;

  // Custom CSS for resize handles
  const resizeHandleStyle = `
    .react-resizable-handle {
      background: #007bff !important;
      border: 2px solid #fff !important;
      border-radius: 3px !important;
      width: 12px !important;
      height: 12px !important;
      opacity: 0.8 !important;
    }
    .react-resizable-handle:hover {
      opacity: 1 !important;
      background: #0056b3 !important;
    }
  `;

  return (
    <>
      <style>{resizeHandleStyle}</style>
    <Draggable
      position={position}
      onStart={handleStart}
      onDrag={handleDrag}
      onStop={handleStop}
      bounds="body" // This ensures the component won't go off-screen
      handle=".drag-handle" // Only allow dragging from the handle
    >
      <Box
        position="fixed"
        top="0"
        left="0"
        zIndex={9999}
        bg="white"
        border="1px solid #ccc"
        borderRadius="md"
        shadow="md"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <ResizableBox
          width={isMinimized ? 300 : size.width}
          height={isMinimized ? 100 : size.height}
          minConstraints={[300, isMinimized ? 100 : 400]}
          maxConstraints={[800, isMinimized ? 100 : 800]}
          resizeHandles={isMinimized ? [] : ["se", "sw", "ne", "nw"]}
          onResizeStop={handleResize}
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
          }}
        >
          <Box position="relative" w="100%" h="100%">
            {/* Drag handle - only this area is draggable */}
            <Box
              className="drag-handle"
              position="absolute"
              top="0"
              left="0"
              right="0"
              height="40px"
              bg={isMinimized ? "blue.100" : "gray.100"}
              borderBottom="1px solid #ccc"
              cursor="move"
              zIndex="tooltip"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.600" title={isMinimized ? "Recording in progress - expand to control" : "Audio Recorder"}>
                {isMinimized ? "Recording..." : "Audio Recorder"} {isMinimized && <span style={{color: 'red', fontSize: '12px'}} title="Recording in progress">●</span>}
              </Text>
              <Box display="flex" gap={1}>
                <IconButton
                  icon={isMinimized ? <FiMaximize2 /> : <FiMinimize2 />}
                  size="sm"
                  aria-label={isMinimized ? "Maximize component" : "Minimize component"}
                  onClick={handleMinimize}
                />
                <IconButton
                  icon={<FiX />}
                  size="sm"
                  aria-label="Close component"
                  onClick={handleClose}
                />
              </Box>
            </Box>
            {/* Content area - not draggable */}
            <Box 
              pt="40px" 
              h={isMinimized ? "60px" : "calc(100% - 40px)"} 
              overflow="auto"
              transition="height 0.3s ease"
            >
              {React.cloneElement(children, { isMinimized })}
            </Box>
          </Box>
        </ResizableBox>
      </Box>
    </Draggable>
    </>
  );
};

export default PersistentComponent;
